// Bookmark toggle — shared logic for toggling item bookmarks in progress.
//
// Extracted from flashcards-view.tsx and use-quiz-session.ts which had
// structurally identical bookmark toggle logic (~12 lines each).
//
// The function is a pure operation over updateProgress + refreshProgress,
// requiring only item identity and type.

import type { CourseProgress } from '@examforge/shared/schemas';

export type BookmarkType = 'mcq' | 'tf' | 'flashcard';

/**
 * Toggle a bookmark for an item in the progress store.
 *
 * - If bookmarked: removes it.
 * - If not bookmarked: adds with type, caseId, and timestamp.
 *
 * @param draftId - Course draft ID for updateProgress.
 * @param itemId - The item to bookmark/unbookmark.
 * @param itemType - 'mcq', 'tf', or 'flashcard'.
 * @param itemCaseId - Chapter ID for the item (null if course-wide).
 * @param updateProgress - IDB progress mutator.
 * @param refreshProgress - Zustand store refresher.
 */
export async function toggleBookmark(
  draftId: string,
  itemId: string,
  itemType: BookmarkType,
  itemCaseId: string | null,
  updateProgress: (id: string, fn: (p: CourseProgress) => CourseProgress | void) => Promise<CourseProgress>,
  refreshProgress: () => Promise<void>,
): Promise<void> {
  await updateProgress(draftId, (p) => {
    if (p.bookmarks[itemId]) {
      delete p.bookmarks[itemId];
    } else {
      p.bookmarks[itemId] = {
        type: itemType,
        caseId: itemCaseId,
        addedAt: new Date().toISOString(),
      };
    }
  });
  await refreshProgress();
}
